/**
 * Editor di formule (CF11) — CL04.
 *
 * Il confine di questo modulo è deliberato e stretto: il client **non
 * interpreta** le formule. Nessun `eval`, nessun `Function`, nessun parser
 * alternativo a quello Python — due parser divergono, e quello autorevole è uno
 * solo.
 *
 * Quello che fa:
 *
 * - mostra la formula con le **label** al posto dei riferimenti, perché è così
 *   che un progettista la legge;
 * - tiene i riferimenti persistiti come `fieldId`, perché è così che si
 *   indirizza un campo;
 * - inserisce un token alla posizione del cursore;
 * - distingue **in attesa**, **valido** e **in errore**, e non mostra un badge
 *   verde prima che il server abbia risposto.
 *
 * La sintassi la valida il server. Un badge verde su una formula che il client
 * ha solo guardato sarebbe una promessa che nessuno ha mantenuto.
 */

import type { FieldDefinition, FormulaDiagnostic, FormulaPreviewResult } from "model/CustomFields";

/** La forma canonica di un riferimento: `@{uuid}`. */
const FIELD_REF = /@\{([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\}/g;

/** Le funzioni ammesse. La grammatica completa è del contratto Python. */
export const ALLOWED_FUNCTIONS = ["ROUND", "IF", "SUM", "DAYS_BETWEEN"] as const;

/** Il token di presentazione di un campo: le graffe sono testo, non sintassi. */
export const tokenFor = (label: string): string => `{${label}}`;

/**
 * Gli id referenziati da un'espressione.
 *
 * Riconosce il riferimento canonico e nient'altro: una label fra graffe **non**
 * è un riferimento, ed è il motivo per cui la presentazione non viene mai
 * riletta come formula.
 */
export function referencedFieldIds(expression: string): string[] {
  const found = new Set<string>();
  for (const match of Array.from(expression.matchAll(FIELD_REF))) {
    found.add(match[1]);
  }
  return Array.from(found);
}

/**
 * La formula come la legge un progettista: riferimenti sostituiti dalle label.
 *
 * Un riferimento a un campo che non esiste più resta **visibile come
 * riferimento** invece di sparire: è un errore di configurazione, non un testo
 * da ripulire, e nasconderlo lo renderebbe irrintracciabile.
 */
export function displayExpression(
  expression: string,
  labelFor: (fieldId: string) => string | undefined
): string {
  return expression.replace(FIELD_REF, (whole, fieldId: string) => {
    const label = labelFor(fieldId);
    return label === undefined ? whole : tokenFor(label);
  });
}

/** Posizioni dei riferimenti nella formula salvata e in quella mostrata. */
function referenceSpans(expression: string, labelFor: (fieldId: string) => string | undefined) {
  let displayOffset = 0;
  let previousEnd = 0;
  return Array.from(expression.matchAll(FIELD_REF), (match) => {
    const canonicalStart = match.index ?? 0;
    displayOffset += canonicalStart - previousEnd;
    const displayStart = displayOffset;
    const label = labelFor(match[1]);
    displayOffset += label === undefined ? match[0].length : tokenFor(label).length;
    previousEnd = canonicalStart + match[0].length;
    return {
      canonicalStart,
      canonicalEnd: previousEnd,
      displayStart,
      displayEnd: displayOffset,
    };
  });
}

function canonicalOffset(
  position: number,
  side: "start" | "end",
  spans: ReturnType<typeof referenceSpans>
): number {
  let shift = 0;
  for (const span of spans) {
    if (position < span.displayStart) break;
    if (position === span.displayStart) return span.canonicalStart;
    if (position < span.displayEnd) {
      return side === "start" ? span.canonicalStart : span.canonicalEnd;
    }
    shift = span.canonicalEnd - span.displayEnd;
  }
  return position + shift;
}

/** Traduce la selezione dell'input leggibile nel valore canonico. */
export function displayedSelectionToCanonical(
  expression: string,
  start: number,
  end: number,
  labelFor: (fieldId: string) => string | undefined
): { start: number; end: number } {
  const spans = referenceSpans(expression, labelFor);
  return {
    start: canonicalOffset(start, "start", spans),
    end: canonicalOffset(end, "end", spans),
  };
}

/** Applica una modifica al testo leggibile preservando gli ID dei token intatti. */
export function editDisplayedExpression(
  expression: string,
  nextDisplay: string,
  labelFor: (fieldId: string) => string | undefined
): string {
  const currentDisplay = displayExpression(expression, labelFor);
  if (currentDisplay === nextDisplay) return expression;

  let start = 0;
  while (start < currentDisplay.length && currentDisplay[start] === nextDisplay[start]) start++;
  let end = currentDisplay.length;
  let nextEnd = nextDisplay.length;
  while (end > start && nextEnd > start && currentDisplay[end - 1] === nextDisplay[nextEnd - 1]) {
    end--;
    nextEnd--;
  }

  // Una modifica dentro la label sostituisce l'intero token con testo visibile:
  // non si associa mai una label ritoccata a un ID diverso per caso.
  for (const span of referenceSpans(expression, labelFor)) {
    if (start > span.displayStart && start < span.displayEnd) start = span.displayStart;
    if (end > span.displayStart && end < span.displayEnd) {
      nextEnd += span.displayEnd - end;
      end = span.displayEnd;
    }
  }

  const selection = displayedSelectionToCanonical(expression, start, end, labelFor);
  return (
    expression.slice(0, selection.start) +
    nextDisplay.slice(start, nextEnd) +
    expression.slice(selection.end)
  );
}

/**
 * I campi disponibili come token, con la label di presentazione.
 *
 * Solo i campi che possono stare in una formula: numerici, date, testi e si/no
 * hanno senso; un campo gestito dal sistema (progressivo), un allegato o un
 * altro calcolato no — non sono operandi componibili. Le formule gia' salvate
 * che citano un calcolato restano leggibili e valutabili dal server: qui si
 * decide solo cosa si puo' **comporre**.
 */
export function availableTokens(fields: readonly FieldDefinition[]): {
  fieldId: string;
  label: string;
  token: string;
}[] {
  return fields
    .filter(
      (field) =>
        !field.deleted &&
        field.type !== "autoincrement" &&
        field.type !== "attachment" &&
        field.type !== "calculated"
    )
    .map((field) => ({
      fieldId: field.fieldId,
      label: field.label,
      token: tokenFor(field.label),
    }));
}

/**
 * Inserisce un riferimento alla posizione del cursore.
 *
 * Restituisce la nuova espressione **e** dove va il cursore dopo: senza la
 * posizione, l'editor riporterebbe il cursore in fondo a ogni token inserito.
 */
export function insertToken(
  expression: string,
  selectionStart: number,
  selectionEnd: number,
  fieldId: string
): { expression: string; cursor: number } {
  const token = `@{${fieldId}}`;
  const before = expression.slice(0, selectionStart);
  const after = expression.slice(selectionEnd);
  return { expression: `${before}${token}${after}`, cursor: before.length + token.length };
}

// ---------------------------------------------------------------------------
// Lo stato dell'anteprima
// ---------------------------------------------------------------------------

export type PreviewState =
  | { kind: "empty" }
  | { kind: "pending" }
  | { kind: "valid"; value: FormulaPreviewResult["value"] }
  | { kind: "invalid"; diagnostics: FormulaDiagnostic[] };

/**
 * Lo stato da mostrare, da ciò che si sa.
 *
 * Un'espressione vuota non è "in attesa": non c'è niente da validare. Una
 * risposta che non è ancora arrivata non è "valida": è **in attesa**, e la
 * differenza è tutto il punto — un badge verde prima della risposta
 * prometterebbe una validazione che non è avvenuta.
 */
export function previewState(
  expression: string,
  response: FormulaPreviewResult | null
): PreviewState {
  if (expression.trim() === "") return { kind: "empty" };
  if (response === null) return { kind: "pending" };
  if (response.valid) return { kind: "valid", value: response.value };
  return { kind: "invalid", diagnostics: response.diagnostics };
}

/**
 * La risposta è ancora quella attesa?
 *
 * L'editor manda una richiesta per ogni modifica; le risposte possono arrivare
 * fuori ordine. Applicare la penultima mostrerebbe il risultato di una formula
 * che l'utente ha già cambiato.
 */
export const isCurrentResponse = (responseId: number, latestId: number): boolean =>
  responseId === latestId;

/** Un contatore di richieste, per riconoscere quella corrente. */
export function nextRequestId(previous: number): number {
  return previous + 1;
}

/**
 * Il primo token problematico, se la diagnostica ne indica uno.
 *
 * Serve a mettere il cursore dove la formula è sbagliata: sapere che è invalida
 * senza sapere dove lascia il progettista a cercare.
 */
export function firstDiagnostic(
  state: PreviewState
): FormulaDiagnostic | null {
  if (state.kind !== "invalid") return null;
  return state.diagnostics[0] ?? null;
}
